import React, { useEffect, useRef, useState } from 'react';

import { ICONS, Icon } from '../invoices/kit';
import {
  ACCEPTED_EXTENSIONS,
  MAX_SHOTS,
  addShots,
  filesFromDrop,
  filesFromPaste,
  formatBytes,
  removeShot,
  roomLeft,
} from './lib/shots';

/**
 * Attaching a picture of the broken thing.
 *
 * Three ways in, because the right one depends on how the screenshot was
 * taken: paste (Cmd+Shift+4 then Cmd+V, which is the whole gesture on a
 * Mac and the reason most of these will arrive), drop, and the picker.
 *
 * The paste listener is on the document rather than on this element: a
 * person who has just taken a screenshot pastes immediately, and asking
 * them to click the dropzone first to focus it is the step that loses
 * them. It is only attached while the modal is open and there is room.
 *
 * Previews are object URLs, revoked when the image is removed and on
 * unmount. Left alone they hold the file in memory for the life of the
 * tab.
 */
export default function ShotPicker({ shots, onChange }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState([]);
  const [previews, setPreviews] = useState({});

  const room = roomLeft(shots);

  const take = (files) => {
    const { shots: next, errors } = addShots(shots, files);
    setRejected(errors);
    if (next !== shots) onChange(next);
  };

  useEffect(() => {
    if (room === 0) return undefined;
    const onPaste = (event) => {
      const files = filesFromPaste(event.clipboardData);
      if (files.length === 0) return;
      // Only swallow the paste once we know it carried an image, so
      // pasting text into the description still works.
      event.preventDefault();
      take(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  // One object URL per file, created once and revoked when it goes.
  useEffect(() => {
    setPreviews((current) => {
      const next = {};
      shots.forEach((shot) => {
        next[shot.id] = current[shot.id] || URL.createObjectURL(shot.file);
      });
      Object.keys(current).forEach((id) => {
        if (!next[id]) URL.revokeObjectURL(current[id]);
      });
      return next;
    });
  }, [shots]);

  useEffect(
    () => () => {
      Object.keys(previews).forEach((id) => URL.revokeObjectURL(previews[id]));
    },
    // Runs on unmount only; previews is read through the closure at that
    // point, which is what we want -- a dependency here would revoke the
    // URLs on every change and blank the thumbnails.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="iv-field">
      <span className="iv-label" id="fb-shots-label">
        Screenshot <span className="fb-optional">optional</span>
      </span>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        multiple
        aria-labelledby="fb-shots-label"
        style={{ display: 'none' }}
        onChange={(event) => {
          take(event.target.files);
          // Reset, or picking the same file twice in a row does nothing.
          event.target.value = '';
        }}
      />

      {shots.length > 0 ? (
        <ul className="fb-shots">
          {shots.map((shot) => (
            <li key={shot.id} className="fb-shot">
              {previews[shot.id] ? (
                <img className="fb-shot__img" src={previews[shot.id]} alt={shot.name} />
              ) : (
                <span className="fb-shot__img" />
              )}
              <span className="fb-shot__meta">
                <span className="fb-shot__name">{shot.name}</span>
                <span className="fb-shot__size iv-num">{formatBytes(shot.size)}</span>
              </span>
              <button
                type="button"
                className="fb-shot__x"
                aria-label={`Remove ${shot.name}`}
                onClick={() => {
                  setRejected([]);
                  onChange(removeShot(shots, shot.id));
                }}
              >
                <Icon path={ICONS.x} size={15} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {room > 0 ? (
        <div
          className={`fb-drop${over ? ' fb-drop--over' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setOver(false);
            take(filesFromDrop(event.dataTransfer));
          }}
        >
          <Icon path={ICONS.plus} size={15} />
          <span className="fb-drop__text">
            <button type="button" className="iv-btn--link" onClick={() => inputRef.current.click()}>
              Choose an image
            </button>
            , drop one here, or just paste — {room} of {MAX_SHOTS} left
          </span>
        </div>
      ) : null}

      {rejected.length > 0 ? (
        <span className="iv-hint iv-hint--error">{rejected.join(' ')}</span>
      ) : (
        <span className="iv-hint">
          A screenshot shows whatever was on your screen, including client names and figures, and
          the internal team can see it. Crop anything you would not share.
        </span>
      )}
    </div>
  );
}
